import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import UpdateSidebar from "../sidebars/updateSidebar";
import { useCopyLaneToDay, useDeleteLane, useLaneByPlanID, useLanesByDay } from "../hooks/useLanes";
import CopyMoveLaneSidebar from "../sidebars/copyMoveLaneSidebar";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import { ReactComponent as ArrowLeftIcon } from "images/arrow_left.svg";
import { ReactComponent as ArrowRightIcon } from "images/arrow_right.svg";
import AddDropModal from "./addDropModal";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import { setPlanId, staticLoadSelector } from "slices/static-load/staticLoadSlice";
import { useDispatch, useSelector } from "react-redux";
import { IState } from "slices";
import { transformLanesToRows } from "../utils/transformLanes";
import { useTerminalDetailsByNumbers } from "../hooks/useTerminalDetailsByNumbers";
import { formatTerminalsByNumber } from "../utils/utils";
import Loading from "ui-components/loading/loading";
import { ReactComponent as ThreeDotsIcon } from "images/three_dots.svg";
import { useWeeks } from "../hooks/useWeek";
import { userSelector } from "slices/user/userSlice";
import { UserUtils } from "app/data/user/userUtils";

import "./dispatchBoard.scss";

const LINEHAUL_ADMINS_GROUP = "Linehaul Admins";
const DAY_COLUMN_COUNT = 7;
const FLOATING_GAP = 6;
const VIEWPORT_PADDING = 8;

const computeFloatingPosition = (
  anchorRect: DOMRect,
  floatingWidth: number,
  floatingHeight: number
) => {
  const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PADDING;
  const spaceAbove = anchorRect.top - VIEWPORT_PADDING;

  let top: number;
  if (spaceBelow >= floatingHeight + FLOATING_GAP) {
    top = anchorRect.bottom + FLOATING_GAP;
  } else if (spaceAbove >= floatingHeight + FLOATING_GAP) {
    top = anchorRect.top - floatingHeight - FLOATING_GAP;
  } else if (spaceBelow >= spaceAbove) {
    top = Math.max(
      VIEWPORT_PADDING,
      window.innerHeight - floatingHeight - VIEWPORT_PADDING
    );
  } else {
    top = VIEWPORT_PADDING;
  }

  let left = anchorRect.left;
  if (left + floatingWidth > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - floatingWidth - VIEWPORT_PADDING;
  }
  left = Math.max(VIEWPORT_PADDING, left);

  return { top, left };
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Dispatch {
  id: string;
  airport: string;
  destination: string;
  destinationCode?: string;
  origin?: string;
  stops?: string;
  stopCodes?: string[];
  time: string;
  carrier?: string;
  price?: string;
  color?: "blue" | "purple" | "green";
  driverInitial?: string;
  driverColor?: string;
  drops?: number;
  carrierCost?: string;
  carrierRate?: string;
}

export interface Lane {
  origin: string;
  stops?: string | undefined;
  destination: string;
  departure: string;
  carrier: string;
  drops: number;
  carrierRate?: string;
}

export interface Row {
  label: string;
  originCode: string;
  dispatches: (Dispatch | null)[];
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────

interface TooltipProps {
  lane: Lane;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
}

const Tooltip: React.FC<TooltipProps> = ({ lane, anchorRef, onClose }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current || !tooltipRef.current) {
      return;
    }

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    setPos(computeFloatingPosition(anchorRect, tooltipRect.width, tooltipRect.height));
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, lane]);

  useEffect(() => {
    const handleScroll = () => onClose();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updatePosition); // or also onClose if you prefer

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [onClose, updatePosition]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node) &&
        !anchorRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      ref={tooltipRef}
      className="dispatch-tooltip"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <div className="dispatch-tooltip__header">
        <div className="dispatch-tooltip__title">{lane.destination}</div>
        <div className="dispatch-tooltip__subtitle">
          {lane.origin} {lane.stops ? ` → ${lane.stops}` : ""} → {lane.destination}
        </div>
      </div>
      <div className="dispatch-tooltip__body">
        <div className="dispatch-tooltip__row">
          <span className="dispatch-tooltip__label">Departure</span>
          <span className="dispatch-tooltip__value">{lane.departure}</span>
        </div>
        <div className="dispatch-tooltip__row">
          <span className="dispatch-tooltip__label">Carrier</span>
          <span className="dispatch-tooltip__value">{lane.carrier}</span>
        </div>
        <div className="dispatch-tooltip__row">
          <span className="dispatch-tooltip__label">Drops</span>
          <span className="dispatch-tooltip__value">{lane.drops}</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Context Menu ─────────────────────────────────────────────────────────────

interface ContextMenuProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onViewLaneDetails: () => void;
  onEditLane: () => void;
  onDuplicateLane: () => void;
  onMoveToLane: () => void;
  onAddDrop: () => void;
  onRemoveDispatch: () => void;
  readOnly?: boolean;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ anchorRef, onClose, onViewLaneDetails, onEditLane, onDuplicateLane, onMoveToLane, onAddDrop, onRemoveDispatch, readOnly = false }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current || !menuRef.current) {
      return;
    }

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const { top } = computeFloatingPosition(anchorRect, menuRect.width, menuRect.height);

    let left = anchorRect.left - menuRect.width + anchorRect.width;
    if (left + menuRect.width > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - menuRect.width - VIEWPORT_PADDING;
    }
    left = Math.max(VIEWPORT_PADDING, left);

    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);
  
  useEffect(() => {
    const handleScroll = () => onClose();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updatePosition); // or also onClose if you prefer

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [onClose, updatePosition]);

  useEffect(() => {
    // Defer so the opening interaction can't immediately count as an outside click.
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [onClose]);

  const items = [
    { label: "View Lane Details", danger: false, onClick: onViewLaneDetails },
    ...(!readOnly ? [
      { label: "Edit Lane", danger: false, onClick: onEditLane },
      { label: "Duplicate", danger: false, onClick: onDuplicateLane },
      { label: "Move to", danger: false, onClick: onMoveToLane },
      { label: "Add Drop", danger: false, onClick: onAddDrop },
      { label: "Remove Dispatch", danger: true, onClick: onRemoveDispatch },
    ] : []),
  ];

  return createPortal(
    <div
      ref={menuRef}
      className="dispatch-context-menu"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i === items.length - 1 && items.length > 1 && (
            <div className="dispatch-context-menu__divider" />
          )}
          <button
            type="button"
            className={`dispatch-context-menu__item${item.danger ? " dispatch-context-menu__item--danger" : ""}`}
            onMouseDown={(e) => {
              // Fire on mousedown so the action isn't lost if the menu unmounts before click.
              e.preventDefault();
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body
  );
};

// ─── Dispatch Chip ────────────────────────────────────────────────────────────

export interface DispatchChipProps {
  dispatch: Dispatch;
  rowLabel: string;
  refetchLanesByDay: () => void;
  showDestinationAirport?: boolean;
  readOnly?: boolean;
}

export const DispatchChip: React.FC<DispatchChipProps> = ({ dispatch, rowLabel, refetchLanesByDay, showDestinationAirport = true, readOnly = false }) => {
  const selectedDivision = useSelector(
    (state: IState) => staticLoadSelector(state).selectedDivision
  );
  const weekStartDate = useSelector(
    (state: IState) => staticLoadSelector(state).weekStartDate
  );
  const userState = useSelector(userSelector);
  const isLinehaulManagementEnabled = UserUtils.hasAssignedGroup(
    userState.profile,
    LINEHAUL_ADMINS_GROUP
  );
  const isReadOnly = readOnly || !isLinehaulManagementEnabled;
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [isOpenUpdateSidebar, setIsOpenUpdateSidebar] = useState<boolean>(false);
  const [isOpenCopyMoveLaneSidebar, setIsOpenCopyMoveLaneSidebar] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<'view' | 'edit' | 'create'>('view');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [copyType, setCopyType] = useState<'copy' | 'move'>('copy');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const deleteLaneMutation = useDeleteLane();
  const copyLaneToDayMutation = useCopyLaneToDay();
  
  const dispatchEffect = useDispatch();
  // Only fetch lane details when this chip actually needs them.
  const shouldFetchLane = isOpenUpdateSidebar || isModalOpen;
  const { data: laneByPlanId, isLoading: isLoadingLaneByPlanID, refetch: refetchLaneByPlanID } = useLaneByPlanID(
    shouldFetchLane ? dispatch.id : "",
    selectedDivision,
    weekStartDate
  );

  const terminalNumbers = useMemo(() => {
    if (!shouldFetchLane) return [];
    return laneByPlanId?.drops?.map((d) => d.terminalNumber?.toString()).filter((n) => n != null && n !== "") ?? [];
  }, [shouldFetchLane, laneByPlanId?.drops]);

  const { data: terminalDetails } = useTerminalDetailsByNumbers(terminalNumbers);
  const terminalMap = useMemo(
    () => formatTerminalsByNumber(terminalDetails),
    [terminalDetails]
  );

  const chipRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const lane: Lane = {
    origin: dispatch.origin ?? rowLabel,
    stops: dispatch.stops ?? undefined,
    destination: dispatch.destination,
    departure: dispatch.time,
    carrier: dispatch.carrier ?? "—",
    drops: dispatch.drops ?? 0,
  };

  const closeMenu = useCallback(() => setShowMenu(false), []);

  const openUpdateSidebar = useCallback((mode: 'view' | 'edit') => {
    // Set local open state first so a Redux re-render can't wipe a pending open.
    setIsEditMode(mode);
    setShowMenu(false);
    setIsOpenUpdateSidebar(true);
    dispatchEffect(setPlanId(dispatch.id));
  }, [dispatchEffect, dispatch.id]);

  const openCopyMoveSidebar = useCallback((type: 'copy' | 'move') => {
    setCopyType(type);
    setIsOpenUpdateSidebar(false);
    setShowMenu(false);
    setIsOpenCopyMoveLaneSidebar(true);
    dispatchEffect(setPlanId(dispatch.id));
  }, [dispatchEffect, dispatch.id]);

  const openAddDropModal = useCallback(() => {
    setShowMenu(false);
    setIsModalOpen(true);
    dispatchEffect(setPlanId(dispatch.id));
  }, [dispatchEffect, dispatch.id]);

  const openDeleteModal = useCallback(() => {
    setShowMenu(false);
    setShowDeleteModal(true);
    dispatchEffect(setPlanId(dispatch.id));
  }, [dispatchEffect, dispatch.id]);

  const handleConfirmDelete = () => {
    deleteLaneMutation.mutate(dispatch.id, {
      onSuccess: () => {
        refetchLanesByDay();
        setShowDeleteModal(false);
        setShowMenu(false);
      },
    });
  };

  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(dispatch.carrierCost) || 0);

  return (
    <>
      <div
        ref={chipRef}
        className={`dispatch-chip dispatch-chip--${dispatch.color ?? "blue"}`}
        onClick={() => setShowTooltip((v) => !v)}
      >

        {showDestinationAirport && <span className="dispatch-chip__airport" title={dispatch.destinationName ?? dispatch.airport} >
          {dispatch.airport}
        </span>}
        <span className="dispatch-chip__time">{dispatch.time}</span>

        {dispatch.carrier && (
          <span className="dispatch-chip__carrier" title={dispatch.carrier}>{dispatch.carrier}</span>
        )}

        <span className="dispatch-chip__price">{formattedPrice}</span>

        <button
          ref={menuBtnRef}
          className="dispatch-chip__more"
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip(false);
            setShowMenu((v) => !v);
          }}
          aria-label="More options"
        >
          <ThreeDotsIcon />
        </button>
      </div>

      {showTooltip && (
        <Tooltip
          lane={lane}
          anchorRef={chipRef}
          onClose={() => setShowTooltip(false)}
        />
      )}
      {showMenu && (
        <ContextMenu
          anchorRef={menuBtnRef}
          onClose={closeMenu}
          onViewLaneDetails={() => openUpdateSidebar('view')}
          onEditLane={() => openUpdateSidebar('edit')}
          onDuplicateLane={() => openCopyMoveSidebar('copy')}
          onMoveToLane={() => openCopyMoveSidebar('move')}
          onAddDrop={openAddDropModal}
          onRemoveDispatch={openDeleteModal}
          readOnly={isReadOnly}
        />
      )}
      {showDeleteModal && createPortal(
        <ConfirmationModal
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          opened={showDeleteModal}
          spinner={deleteLaneMutation.isLoading}
        >
          <div className="confirmation-modal__content">
            <AlertIcon />
            <div>Are you sure you want to delete this dispatch?</div>
          </div>
        </ConfirmationModal>,
        document.body
      )}
      {isOpenUpdateSidebar && createPortal(
        <UpdateSidebar
          isOpen
          onClose={() => {
            setIsOpenUpdateSidebar(false);
            setShowMenu(false);
          }}
          title={isEditMode === 'edit' ? "Edit Lane" : isEditMode === 'create' ? "Create Lane" : "Lane Details"}
          data={laneByPlanId || { lane: {}, drops: [] }}
          isLoading={isLoadingLaneByPlanID}
          terminalMap={terminalMap}
          isEditMode={isEditMode}
          refetchLane={refetchLaneByPlanID}
          refetchLaneByDay={refetchLanesByDay}
          handleAddDrop={() => setIsModalOpen(true)}
          onSwitchToEdit={isReadOnly ? undefined : () => setIsEditMode('edit')}
        />,
        document.body
      )}
      {isOpenCopyMoveLaneSidebar && createPortal(
        <CopyMoveLaneSidebar
          isOpen
          onClose={() => {
            setIsOpenCopyMoveLaneSidebar(false);
            setShowMenu(false);
          }}
          isLoading={false}
          copyLaneToDayMutation={copyLaneToDayMutation}
          type={copyType}
          refetchLaneByDay={refetchLanesByDay}
        />,
        document.body
      )}
      {isModalOpen && createPortal(
        <AddDropModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            refetchLaneByPlanID();
            refetchLanesByDay();
          }}
        />,
        document.body
      )}
    </>
  );
};

// ─── Board Header ─────────────────────────────────────────────────────────────

interface BoardHeaderProps {
  currentDate: moment.Moment | null;
  weekStartDate: string;
  onPrevDay: () => void;
  onNextDay: () => void;
  /** When true, day was locked via the Day of week filter — prev/next are disabled */
  dayNavLocked?: boolean;
}

const BoardHeader: React.FC<BoardHeaderProps> = ({
  currentDate,
  weekStartDate,
  onPrevDay,
  onNextDay,
  dayNavLocked = false,
}) => {
  const weekStart = moment(weekStartDate, "YYYY-MM-DD", true);
  const selectedDay = currentDate?.isValid() ? currentDate : null;
  const hasValidWeek = weekStart.isValid() && !!selectedDay;
  const weekEnd = weekStart.clone().add(6, "days");
  const isAtWeekStart = !!selectedDay?.isSameOrBefore(weekStart, "day");
  const isAtWeekEnd = !!selectedDay?.isSameOrAfter(weekEnd, "day");
  const selectedDayLabel = selectedDay ? selectedDay.format("dddd, MMMM D") : "";
  const navDisabled = !hasValidWeek || dayNavLocked;

  return (
    <div className="dispatch-board__header">
      <div className="dispatch-board__origin-cell">
        <span className="dispatch-board__origin-label">Origin</span>
      </div>

      <div className="dispatch-board__day-columns">
        {Array.from({ length: DAY_COLUMN_COUNT }, (_, index) => (
          <div
            key={index}
            className={`dispatch-board__day-column${index === 0 ? "" : " dispatch-board__day-column--empty"}`}
          >
            {index === 0 && (
              <div className="dispatch-board__date-nav">
                <button
                  type="button"
                  className="dispatch-board__nav-btn"
                  onClick={onPrevDay}
                  disabled={navDisabled || isAtWeekStart}
                  aria-label="Previous day"
                >
                  <ArrowLeftIcon />
                </button>
                <span className="dispatch-board__date">{selectedDayLabel}</span>
                <button
                  type="button"
                  className="dispatch-board__nav-btn"
                  onClick={onNextDay}
                  disabled={navDisabled || isAtWeekEnd}
                  aria-label="Next day"
                >
                  <ArrowRightIcon />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Add Dispatch Cell ────────────────────────────────────────────────────────

interface AddDispatchContext {
  dayOfWeek: number;
  originAirportCode: string;
  originName: string;
}

const AddDispatchCell: React.FC<{ onAddDispatch: () => void }> = ({ onAddDispatch }) => (
  <button type="button" className="dispatch-add-cell" onClick={onAddDispatch}>+ Add Dispatch</button>
);


// ─── Main Component ───────────────────────────────────────────────────────────

export type DispatchBoardFilters = {
  origins: string[];
  stops: string[];
  destinations: string[];
  /** ISO weekday 1–7 (Mon–Sun); empty means no day filter */
  day: string;
  region?: string;
};

type DispatchBoardProps = {
  filters?: DispatchBoardFilters;
  readOnly?: boolean;
};

const parseWeekStart = (weekStartDate: string) =>
  moment(weekStartDate, "YYYY-MM-DD", true);

const DispatchBoard: React.FC<DispatchBoardProps> = ({ filters, readOnly }) => {
  const staticLoadState = useSelector(staticLoadSelector);
  const userState = useSelector(userSelector);
  const isLinehaulManagementEnabled = UserUtils.hasAssignedGroup(
    userState.profile,
    LINEHAUL_ADMINS_GROUP
  );
  const isReadOnly = readOnly ?? !isLinehaulManagementEnabled;
  const weekStart = useMemo(
    () => parseWeekStart(staticLoadState.weekStartDate),
    [staticLoadState.weekStartDate]
  );
  const [currentDate, setCurrentDate] = useState<moment.Moment | null>(() =>
    weekStart.isValid() ? weekStart.clone() : null
  );
  const [isOpenAddDispatchModal, setIsOpenAddDispatchModal] = useState<boolean>(false);
  const [addDispatchContext, setAddDispatchContext] = useState<AddDispatchContext | null>(null);
  // API / weekOptions use Monday=1 … Sunday=7 (moment isoWeekday)
  const dayOfWeek = currentDate?.isValid() ? currentDate.isoWeekday() : 1;

  const { data: weeks } = useWeeks();

  const currentWeek = useMemo(
    () => weeks?.find((w) => w.weekStartDate === staticLoadState.weekStartDate),
    [weeks, staticLoadState.weekStartDate]
  );

  const canAddDispatch =
    !isReadOnly &&
    currentWeek?.status === "ACTIVE" &&
    currentWeek?.scheduleType === "WORKING";

  const { data: lanesByDay, isLoading: isLoadingLanesByDay, refetch: refetchLanesByDay } = useLanesByDay(
    staticLoadState.selectedDivision,
    weekStart.isValid() ? weekStart.format("YYYY-MM-DD") : "",
    filters?.region
  );

  const selectedDayFilter = filters?.day ?? "";
  const dayNavLocked = !!selectedDayFilter;

  const rows = useMemo(() => {
    const selectedOrigins = filters?.origins ?? [];
    const selectedStops = filters?.stops ?? [];
    const selectedDestinations = filters?.destinations ?? [];
    const dayRows = transformLanesToRows(lanesByDay, dayOfWeek);

    return dayRows
      .filter(
        (row) =>
          selectedOrigins.length === 0 || selectedOrigins.includes(row.originCode)
      )
      .map((row) => {
        if (selectedDestinations.length === 0 && selectedStops.length === 0) {
          return row;
        }

        return {
          ...row,
          dispatches: row.dispatches.filter((dispatch) => {
            if (!dispatch) return false;

            const matchesDestination =
              selectedDestinations.length === 0 ||
              (!!dispatch.destinationCode &&
                selectedDestinations.includes(dispatch.destinationCode));

            const matchesStops =
              selectedStops.length === 0 ||
              selectedStops.some((stop) =>
                (dispatch.stopCodes ?? []).includes(stop)
              );

            return matchesDestination && matchesStops;
          }),
        };
      })
      .filter((row) => row.dispatches.length > 0);
  }, [lanesByDay, dayOfWeek, filters?.origins, filters?.stops, filters?.destinations]);

  useEffect(() => {
    const nextWeekStart = parseWeekStart(staticLoadState.weekStartDate);
    setCurrentDate(nextWeekStart.isValid() ? nextWeekStart.clone() : null);
  }, [staticLoadState.weekStartDate]);

  // Jump board date to the filtered weekday within the current week
  useEffect(() => {
    const dayNum = Number(selectedDayFilter);
    if (!selectedDayFilter || dayNum < 1 || dayNum > 7 || !weekStart.isValid()) return;
    setCurrentDate(weekStart.clone().isoWeekday(dayNum));
  }, [selectedDayFilter, weekStart]);

  const prevDay = () => {
    if (dayNavLocked) return;
    setCurrentDate((d) => (d ? d.clone().subtract(1, "day") : d));
  };
  const nextDay = () => {
    if (dayNavLocked) return;
    setCurrentDate((d) => (d ? d.clone().add(1, "day") : d));
  };

  const handleOpenAddDispatch = useCallback((row: Row) => {
    setAddDispatchContext({
      dayOfWeek,
      originAirportCode: row.originCode,
      originName: row.label,
    });
    setIsOpenAddDispatchModal(true);
  }, [dayOfWeek]);

  const handleCloseAddDispatch = useCallback(() => {
    setIsOpenAddDispatchModal(false);
    setAddDispatchContext(null);
  }, []);

  return (
    <div className="dispatch-board">
      <BoardHeader
        currentDate={currentDate}
        weekStartDate={staticLoadState.weekStartDate}
        onPrevDay={prevDay}
        onNextDay={nextDay}
        dayNavLocked={dayNavLocked}
      />
      {/* Rows */}
      <div className="dispatch-board__body">
        {isLoadingLanesByDay ? (
          <div className="dispatch-board__loading">
            <Loading isLoading={isLoadingLanesByDay} />
          </div>
        ) : (rows.length === 0 ? (
          <div className="dispatch-board__empty">No dispatches</div>
        ) : (
          rows.map((row, rowIdx) => (
            <div key={rowIdx} className="dispatch-board__row">
              <div className="dispatch-board__row-label" title={row.label}>{row.label}</div>
              <div className="dispatch-board__chips">
                {row.dispatches.map((dispatch, colIdx) =>
                  dispatch ? (
                    <DispatchChip
                      key={dispatch.id}
                      dispatch={dispatch}
                      rowLabel={row.label}
                      refetchLanesByDay={refetchLanesByDay}
                      readOnly={isReadOnly}
                    />
                  ) : (
                    <div key={`empty-${colIdx}`} className="dispatch-chip-gap" />
                  )
                )}
                {canAddDispatch && (
                  <AddDispatchCell onAddDispatch={() => handleOpenAddDispatch(row)} />
                )}
              </div>
            </div>
          ))
        ))}
      </div>
      {!isReadOnly && (
        <UpdateSidebar
          isOpen={isOpenAddDispatchModal}
          onClose={handleCloseAddDispatch}
          title="Add Dispatch"
          data={{
            lane: {
              dayOfWeek: addDispatchContext?.dayOfWeek ?? dayOfWeek,
              originAirportCode: addDispatchContext?.originAirportCode,
              originName: addDispatchContext?.originName,
            },
            drops: [],
          }}
          isLoading={false}
          terminalMap={{}}
          isEditMode={'add-dispatch'}
          refetchLane={() => { }}
          refetchLaneByDay={refetchLanesByDay}
          handleAddDrop={() => { }}
        />
      )}
    </div>
  );
};

export default DispatchBoard;
